import React from 'react'
import { UserType } from '../../../providers/context'
import { Button, ButtonShape } from '../../../components/simple/button'
import { IconCheckMark, IconEmail, IconPhone, IconText, IconTimes, IconWhatsapp } from '../../../icons/library'
import { UserBadge } from '../../home/messageTags'
import { usePreview } from '../preview'
import { RemoteTable, VisibleColumnDef } from '../../../components/table'
import { DelayedTextInput } from '../../../components/form/textInput'
import { ColumnDef } from '@tanstack/react-table'
import { DateWrapper, formatDateForExport } from '../../../components/simple/date'
import { Tooltip } from '../../../components/simple/tooltip'
import { MessageInfo, MessageInfoFilter } from './messageInfo'
import { Languages } from '../../../providers/languages'
import { capitalize } from '../../../utils'

interface SentMessage {
  id: number
  type: string
  whatsapp?: boolean
  externalContact: string
  delivered_at?: string
  undelivered?: boolean
  badNumber?: boolean
  massAlertStatus?: string
}
interface MessageInfoRow {
  name: string
  userType: UserType
  regardingStudents: string[]
  isRead: boolean
  unsureRead: boolean
  timeRead?: string
  language: string
  sentMessages: SentMessage[]
  sentToExtraNote?: string
  deliveredMessage: string
}

const SentMessageIcon: React.FC<{ sentMessage: MessageInfoRow['sentMessages'][0] }> = ({ sentMessage }) => {
  const { showSentMessagePreview } = usePreview()
  if (sentMessage.type === 'email')
    return (
      <IconEmail
        size="lg"
        className="cursor-pointer text-email"
        onClick={() => showSentMessagePreview(sentMessage.id)}
      />
    )
  if (sentMessage.whatsapp)
    return (
      <IconWhatsapp
        size="lg"
        className="cursor-pointer text-text"
        onClick={() => showSentMessagePreview(sentMessage.id)}
      />
    )
  if (sentMessage.type === 'text')
    return (
      <IconText size="lg" className="cursor-pointer text-text" onClick={() => showSentMessagePreview(sentMessage.id)} />
    )
  if (sentMessage.type === 'phone')
    return (
      <IconPhone
        size="lg"
        className="cursor-pointer text-phone"
        onClick={() => showSentMessagePreview(sentMessage.id)}
      />
    )
  return null
}

const SentMessageContact: React.FC<{ sentMessage: MessageInfoRow['sentMessages'][0] }> = ({ sentMessage }) => {
  const { showSentMessagePreview } = usePreview()
  return (
    <span className="cursor-pointer hover:underline" onClick={() => showSentMessagePreview(sentMessage.id)}>
      {sentMessage.externalContact}
    </span>
  )
}

const getMessageInfoColumns = (messageInfo: MessageInfo) => {
  const c: VisibleColumnDef<MessageInfoRow>[] = [
    {
      id: 'name',
      header: 'Recipient',
      cell: ({ row }) => (
        <div className="text-nowrap">
          {row.original.regardingStudents.length > 0 && (
            <span className="mr-1.5 text-sm">{row.original.regardingStudents.join(', ')}</span>
          )}
          <UserBadge type={row.original.userType} name={row.original.name} />
        </div>
      ),
      accessorFn: (row) =>
        row.name + (row.regardingStudents.length > 0 ? ` (parent of ${row.regardingStudents.join(', ')})` : ''),
    },
    {
      id: 'sentAs',
      header: 'Sent As',
      cell: ({ row }) => (
        <div className="flex gap-1">
          {row.original.sentMessages.map((m) => (
            <SentMessageIcon sentMessage={m} key={m.id} />
          ))}
        </div>
      ),
      accessorFn: (row) => row.sentMessages.map(displayType).join(', '),
    },
    {
      id: 'sentTo',
      header: 'Sent To',
      cell: ({ row }) => (
        <>
          {row.original.sentMessages.map((m, index) => (
            <React.Fragment key={index}>
              <SentMessageContact sentMessage={m} />
              {index < row.original.sentMessages.length - 1 && ', '}
            </React.Fragment>
          ))}
          {row.original.sentToExtraNote}
        </>
      ),
      enableSorting: false,
      accessorFn: (row) => row.sentMessages.map((sm) => sm.externalContact).join(', ') + (row.sentToExtraNote || ''),
    },
    {
      id: 'readAt',
      header: 'Read At',
      cell: ({ row }) => (
        <div className={row.original.isRead && !row.original.unsureRead ? 'font-bold' : ''}>
          {row.original.timeRead ? (
            <Tooltip
              text={
                row.original.unsureRead ? 'This time may not be accurate because of email client issues.' : undefined
              }
            >
              <DateWrapper detailed date={row.original.timeRead} className={row.original.unsureRead ? 'italic' : ''} />
            </Tooltip>
          ) : row.original.isRead ? (
            'Read'
          ) : (
            ''
          )}
        </div>
      ),
      accessorFn: (row) =>
        row.timeRead
          ? formatDateForExport(row.timeRead) +
            (row.unsureRead ? ' (This time may not be accurate because of email client issues.)' : '')
          : row.isRead
            ? 'Read'
            : '',
    },
    {
      id: 'language',
      header: messageInfo.type === 'phone' ? 'Interpreted' : 'Translated',
      enableSorting: false,
      accessorFn: (row) => (row.language == 'en' ? '' : Languages.find(row.language)?.name),
    },
    {
      id: 'delivered',
      header: 'Delivered',
      cell: ({ row }) =>
        row.original.deliveredMessage === 'Delivered' ? (
          <>
            <IconCheckMark />
            {row.original.sentMessages
              .filter((m) => m.type === 'phone')
              .map((m) =>
                m.massAlertStatus === 'phone_delivered_machine'
                  ? ' (answering machine)'
                  : !m.massAlertStatus
                    ? ' (phone answer status unknown)'
                    : ''
              )
              .join('')}
          </>
        ) : (
          row.original.deliveredMessage
        ),
      accessorKey: 'deliveredMessage',
    },
    getSentAtColumn('email'),
    getSentAtColumn('text'),
    ...(messageInfo.type === 'mass' ? [getSentAtColumn('phone')] : []),
  ]
  return c
}

const getSentAtColumn = (type: string): VisibleColumnDef<MessageInfoRow> => {
  return {
    id: `sentAt${capitalize(type)}`,
    header: `Sent At (${capitalize(type)})`,
    cell: ({ row }) =>
      row.original.sentMessages
        .filter((s) => s.type === type)
        .map((m) => (
          <div key={m.id} className="text-nowrap">
            {renderSentAt(m, (d) => (
              <DateWrapper detailed date={d} />
            ))}
          </div>
        )),
    accessorFn: (row) =>
      row.sentMessages
        .filter((s) => s.type === type)
        .map((m) => renderSentAt(m, formatDateForExport))
        .join(', '),
  }
}

const renderSentAt = <T,>(row: SentMessage, render: (date: string) => T) => {
  if (row.badNumber) return 'Bad Number'
  if (row.delivered_at) return render(row.delivered_at)
  if (row.undelivered) return 'Attempted'
  return 'Pending'
}

const displayType = (sm: SentMessage) => (sm.whatsapp ? 'whatsapp' : sm.type)

export const MessageInfoTable: React.FC<{
  messageInfo: MessageInfo
  filters: MessageInfoFilter
  setFilters: React.Dispatch<React.SetStateAction<MessageInfoFilter>>
}> = ({ messageInfo, filters, setFilters }) => {
  const [tableSearch, setTableSearch] = React.useState('')

  const tableRequest = React.useMemo(
    () => ({ url: '/frontend_api/messageInfoTable', data: { messageID: messageInfo.messageID, filters: filters } }),
    [messageInfo.messageID, filters]
  )
  const tableColumns = React.useMemo(
    () => (messageInfo ? getMessageInfoColumns(messageInfo) : undefined),
    [messageInfo]
  )

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          {Object.values(filters).some((value) => value !== undefined) && (
            <div className={`flex items-center self-start bg-warning pl-3`}>
              Only showing&nbsp;
              {Object.entries(filters)
                .filter(([, value]) => value !== undefined)
                .map(([key, value], index, array) => (
                  <React.Fragment key={key}>
                    <b>{value}</b>
                    {index < array.length - 1 && <>,&nbsp;</>}
                  </React.Fragment>
                ))}
              <Button
                className="ml-2"
                buttonStyle=""
                onClick={() => setFilters({})}
                shape={ButtonShape.roundedSmall}
                tooltip="Clear Filters"
              >
                <IconTimes />
              </Button>
            </div>
          )}
        </div>
        <DelayedTextInput
          className="rounded-full px-2 py-[2.5px]"
          placeholder="Search"
          value={tableSearch}
          onChange={setTableSearch}
        />
      </div>
      <RemoteTable
        columns={tableColumns}
        remote={tableRequest}
        search={tableSearch}
        sortable
        exportName={`Message Info ${messageInfo.messageID}`}
        pagination={5}
      />
    </>
  )
}
